// Preferencias de presentación por equipo (no por usuario): tema claro u oscuro y densidad de las tablas.
// Se guardan en el navegador y se aplican como atributos de <html> para que las hojas de estilo respondan.
import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';
const KEYS = { theme: 'teo.theme', density: 'teo.density' } as const;

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return (allowed as readonly string[]).includes(v ?? '') ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export const storedTheme = (): Theme => read(KEYS.theme, ['light', 'dark'] as const, 'light');
export const storedDensity = (): Density => read(KEYS.density, ['comfortable', 'compact'] as const, 'comfortable');

export function applyPrefs(theme: Theme, density: Density) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.dataset.density = density;
}

export function usePrefs() {
  const [theme, setThemeState] = useState<Theme>(storedTheme);
  const [density, setDensityState] = useState<Density>(storedDensity);
  useEffect(() => applyPrefs(theme, density), [theme, density]);
  const save = (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* sin almacenamiento: la preferencia vale para esta visita */
    }
  };
  const toggleTheme = useCallback(() => setThemeState((t) => { const n = t === 'dark' ? 'light' : 'dark'; save(KEYS.theme, n); return n; }), []);
  const toggleDensity = useCallback(() => setDensityState((d) => { const n = d === 'compact' ? 'comfortable' : 'compact'; save(KEYS.density, n); return n; }), []);
  return { theme, density, toggleTheme, toggleDensity };
}
